import { createContext, useContext, ReactNode, JSX } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useQuery, gql } from '@zenbusiness/application-commons-graphql';
import { useError } from '@zenbusiness/application-commons-logging';
import PageError from '@zenbusiness/zui/PageError';
import PageLoading from '@zenbusiness/zui/PageLoading';

import { REQUEST_BANKING_PRODUCT_OFFER_MUTATION } from '#client/mutations/product';

const InitialDataContext = createContext(null);
InitialDataContext.displayName = 'InitialDataContext';

export const INITIAL_DATA_QUERY = gql`
  query bankingInitialData($businessEntityUuid: UUID!) {
    bankingApplication(input: { businessEntityUuid: $businessEntityUuid }) {
      status
      applicationDataId
      applicationId
      initialPaymentStatus
      idTheftScore
    }
    orderTasksByBusinessEntity(businessEntityUuid: $businessEntityUuid) {
      status
      type
      createdAt
    }
    mfaStatus {
      isEnrolled
      isValidMfa
      isEnabled
    }
  }
`;

/**
 * InitialDataProvider
 * Fetches and manages basic banking application, order tasks, and MFA status data and provides it via context.
 *
 * Renders:
 *   - A loading indicator while fetching data
 *   - An error page if fetching fails
 *   - Its children wrapped in InitialDataContext.Provider when data is ready
 *
 * @param {ReactNode} children - Children to render
 * @returns {JSX.Element} - InitialDataContext context
 */
function InitialDataProvider({ children }) {
  const { businessEntityUuid } = useBusinessEntity();
  const request = useQuery(INITIAL_DATA_QUERY, {
    noThrow: true,
    variables: {
      businessEntityUuid
    },
    refetchAfterMutations: [REQUEST_BANKING_PRODUCT_OFFER_MUTATION],
    skipCache: true
  });

  useError(request.error);

  if (request.error) {
    return <PageError />;
  }
  if (request.loading) {
    return <PageLoading />;
  }

  return <InitialDataContext.Provider value={request}>{children}</InitialDataContext.Provider>;
}

function useInitialDataContext() {
  const context = useContext(InitialDataContext);
  if (!context) {
    throw new Error('useInitialDataContext must be used within a InitialDataProvider');
  }
  return context;
}

export { InitialDataProvider, useInitialDataContext };
